import padStart from 'lodash/padStart'

import dateUtil from 'services/dateUtil'
import {
  LocaleInterface,
  TranslationMapType,
  TranslationOptionsType,
} from 'types'

/**
 * Text/translations handler
 */
export default class Locale implements LocaleInterface {
  lang
  translations
  options

  constructor(
    lang: string = 'en',
    translations: TranslationMapType = {},
    options: TranslationOptionsType
  ) {
    this.lang = lang
    this.translations = translations
    this.options = options
  }

  /**
   * Check if a translation exists
   */
  hasTranslation(key) {
    return key && this.translations && key in this.translations
  }

  /**
   * Translate key to string, with parameter substitution.
   * Parameters in curly brackets are recursively translated.
   */
  translate(key, params?) {
    if (key && this.translations && this.translations[key]) {
      let translation = this.translations[key]

      // Transform selection object
      if (typeof translation === 'object') {
        const { param, ...pluralForms } = translation
        translation = this.selectTranslation(
          params[param || 'selector'],
          pluralForms
        )
      }

      if (params) {
        for (const p in params) {
          if (params.hasOwnProperty(p)) {
            // Detect recursive translations
            if (
              typeof params[p] === 'string' &&
              params[p].startsWith('{') &&
              params[p].endsWith('}')
            ) {
              params[p] = this.translate(
                params[p].substring(0, params[p].length - 1).substring(1)
              )
            }
            translation = translation.replace(`{${p}}`, params[p])
          }
        }
      }
      return translation
    }
    return key
  }

  /**
   * Select a translation based on a selector parameter.
   * Selectors are compared in order and return the first match.
   *
   * Possible selector patterns:
   * [string] match
   * ~[string] contains
   * true|false
   * =|<|>|=>|<=[int] integer comparison
   * _ default match
   */
  selectTranslation(selector, selections) {
    const translation = ''
    for (const key of Object.keys(selections)) {
      const keyMatch = key.match(/^([<>=~]*)\s*(.*)$/i)
      if (!keyMatch) break
      const [operand, value] = keyMatch.slice(1)
      switch (operand) {
        case '':
          if (value === '_') return selections[key]
          if (value === 'true' && selector === true) return selections[key]
          if (value === 'false' && selector === false) return selections[key]
          if (selector === value) return selections[key]
          break
        case '~':
          if (selector.includes(value)) return selections[key]
          break
        case '=':
          if (selector === parseInt(value, 10)) return selections[key]
          break
        case '>':
          if (selector > parseInt(value, 10)) return selections[key]
          break
        case '<':
          if (selector < parseInt(value, 10)) return selections[key]
          break
        case '>=':
          if (selector >= parseInt(value, 10)) return selections[key]
          break
        case '<=':
          if (selector <= parseInt(value, 10)) return selections[key]
          break
        default:
          break
      }
    }
    return translation
  }

  /**
   * Format localized date/time
   */
  formatDatetime(utcDatetime, format = 'YYYY-MM-DD hh:mm:ss') {
    const date =
      typeof utcDatetime === 'number'
        ? dateUtil.getSweDateTimeFromMillis(utcDatetime)
        : dateUtil.getSweDateTimeFromISO(utcDatetime)
    const codes = {
      YYYY: 'year',
      // Optional full year, only display if not current year
      XXXX: (d) => {
        const { year } = d
        const yearNow = dateUtil.getSweDateTimeNow().year
        return year === yearNow ? '' : year
      },
      MMMM: (d) => this.options.names.months[d.month - 1],
      MMM: (d) => this.options.names.monthsShort[d.month - 1],
      MM: (d) => padStart(d.month, 2, '0'),
      DD: (d) => padStart(d.day, 2, '0'),
      D: (d) => d.day,
      hh: (d) => padStart(d.hour, 2, '0'),
      mm: (d) => padStart(d.minute, 2, '0'),
      ss: (d) => padStart(d.second, 2, '0'),
    }
    // Get format from options if set
    let formatted =
      format in this.options.formats ? this.options.formats[format] : format
    Object.entries(codes).forEach(([code, formatter]) => {
      formatted = formatted.replace(
        code,
        typeof formatter === 'function' ? formatter(date) : date[formatter]
      )
    })
    return formatted
  }

  /**
   * Format number or string number to a price string with fixed decimal digits and grouped main digits
   * Default separator is a non-breaking thin space
   */
  formatPrice(price, decimals = 2, separator = '\u202f') {
    if (price || price === 0) {
      return (
        (typeof price === 'string' ? parseFloat(price) : price)
          .toFixed(decimals)
          .toString()
          // Group digits
          .replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${separator}`)
          .replace('.', this.options.price.decimalSymbol) +
        separator +
        this.options.price.currencyShort
      )
    }
  }

  /**
   * Format number or string number to a rounded price with prefix, eg. 4321 => 4.3kkr
   */
  formatRoundedPrice(price, decimals = 0, separator = '') {
    let formattedPrice = typeof price === 'string' ? parseFloat(price) : price
    let prefix = ''
    if (price >= 1000000) {
      formattedPrice /= 1000000
      prefix = 'M'
    } else if (formattedPrice >= 1000) {
      formattedPrice /= 1000
      prefix = 'k'
    }
    return (
      formattedPrice
        .toFixed(decimals)
        .toString()
        .replace('.', this.options.price.decimalSymbol) +
      separator +
      prefix +
      this.options.price.currencyShort
    )
  }

  formatOrgNumber(orgNumber) {
    return orgNumber ? orgNumber.replace(/^(\d+)(\d{4})$/, '$1-$2') : ''
  }

  formatPersonalId(personalId) {
    return personalId
      ? personalId.replace(/^(\d+)(\d{4})$/, '$1-$2').replace(/\*/gi, '\u2022')
      : ''
  }

  formatZipCode(zipCode) {
    return zipCode ? zipCode.replace(/^(\d+)(\d{2})$/, '$1\u202f$2') : ''
  }
}
